import React, { useState } from 'react';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Divider from "@mui/material/Divider";
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';

import { withTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { findLastIndex, isEqual, isNil, isNull, map } from 'lodash';

import p13nService from "@plannel/services/system/p13n-service";
import debug from '@plannel/utils/debug-util';
import reduxUtil from "@plannel/utils/redux-util";
import tabMenuList from "@plannel/pages/TabMenuList";

import "@plannel/scss/components/TabContainer.scss"

const COMMON = "COMMON";

/*
 * ⚠️ 이 파일의 하위 컴포넌트(TabPanel·CustomContextMenu·TabHeader)는 **반드시 모듈 스코프**에 둔다.
 *   TabContainer 함수 본문 안에서 정의하면 매 렌더마다 함수 참조(= element type)가 새로 만들어져
 *   React 가 "다른 컴포넌트"로 판단하고 **활성 탭 콘텐츠 서브트리를 통째로 unmount → remount** 한다.
 *   TabContainer 는 탭 추가·전환·핀 토글·언어 변경 등으로 자주 리렌더되므로, 같은 탭에 머물러
 *   있어도 화면이 반복 초기화됐다(2026-08-10 Agent Chat 단일화 작업 중 발견·수정).
 */

/*
 * 모듈 단위 CSS 스코프 훅.
 *   현재 용도는 factory-plan 폰트 크기 동결 하나뿐이다(App.scss 의 `[data-module="FP"]`).
 *   페이지 루트로 쓸 수 있는 id 가 없어서 만든 것 — `fp-*` id 들은 전부 그리드·다이어그램
 *   내부 요소라 필터바·카드가 안 걸린다.
 *
 *   reduxKey 접두사를 그대로 쓰지 않는다 — TARGET_INVENTORY_SIMULATION 처럼 접두사가
 *   모듈이 아닌 리프가 있어 `data-module="TARGET"` 같은 거짓 값이 나온다. 실제로 스코프가
 *   필요한 것만 명시적으로 내보낸다. (FP_ 리프 44개 = factory-plan 화면 전체와 1:1.)
 */
const getModuleScope = (reduxKey) => (reduxKey?.startsWith("FP_") ? "FP" : undefined);

const TabPanel = ({ children, value, index, module }) => {
  if (value !== index) return null;
  return (
    <Box
      role="tabpanel"
      id={`scrollable-auto-tabpanel-${index}`}
      aria-labelledby={`scrollable-auto-tab-${index}`}
      sx={{ width: "100%", height: "100%", overflow: "hidden" }}
    >
      <Box id={"tab-body"} data-module={module} sx={{ width: "100%", height: "100%" }}>{children}</Box>
    </Box>
  );
};

const CustomContextMenu = ({ t, data, setData, selectedTabIdx, setSelectedTabIdx, handleClose, getKeys,
                             reduxDispatch, open, setOpen, contextMenu, requestRefresh }) => {
  const tabDatas = [...data];

  const upsertPinTab = datas => {
    const pinDatas = datas.filter(tab => tab?.pin);
    const tabKeys = map(pinDatas, "key");
    const saveData = { pageCd: COMMON, configObj: { tabKeys } };

    p13nService.upsert(saveData)
      .then(() => {
        debug(t("MSG_SaveSuccess"));
      })
      .catch(e => {
        debug(e);
      });
  }

  const handleContextMenuClose = () => {
    setOpen(false);
  };

  const removeReduxViewState = removeData => {
    const reduxKey = removeData?.reduxKey;
    if (reduxKey) {
      const reduxState = reduxUtil.getViewState(reduxKey);
      reduxDispatch(reduxUtil.removeViewState(reduxState));
    }
  }

  const updateReduxTabState = (tabInfos, idx) => {
    const reduxTabState = reduxUtil.createTabsState(getKeys(tabInfos), idx);
    reduxDispatch(reduxUtil.updateTabState(reduxTabState));
  }

  const pinTab = () => {
    const { currentData, dataIndex } = contextMenu;

    const filteredList = tabDatas.filter((_, idx) => idx !== dataIndex);
    const pinDatas = filteredList.filter(tab => tab?.pin);

    filteredList.splice(pinDatas.length, 0, { ...currentData, pin: true });

    setData(filteredList);
    setSelectedTabIdx(pinDatas.length);

    updateReduxTabState(filteredList, pinDatas.length);
    upsertPinTab(filteredList);

    handleContextMenuClose();
  }

  const unpinTap = () => {
    const { currentData, dataIndex } = contextMenu;

    const filteredList = tabDatas.filter((_, idx) => idx !== dataIndex);

    const lastPinIdx = findLastIndex(tabDatas, tab => tab?.pin);
    const firstUnpinIdx = lastPinIdx > -1 ? lastPinIdx : 0;
    filteredList.splice(firstUnpinIdx, 0, { ...currentData, pin: false });

    setData(filteredList);
    setSelectedTabIdx(firstUnpinIdx);

    updateReduxTabState(filteredList, firstUnpinIdx);
    upsertPinTab(filteredList);

    handleContextMenuClose();
  }

  const closeTap = () => {
    handleClose(contextMenu.currentData);

    handleContextMenuClose();
  }

  const closeOtherTabs = () => {
    const { currentData } = contextMenu;

    const pinDatas = tabDatas.filter(tab => tab?.pin);

    let filteredDatas = [];
    let tabIndex = 0;
    if (currentData?.pin) {
      const currentIndex = pinDatas.findIndex(tab => isEqual(tab.key, currentData.key));
      tabIndex = currentIndex;
      filteredDatas = pinDatas;

    } else {
      const datas = [...pinDatas, currentData];
      tabIndex = datas.length - 1;
      filteredDatas = datas;
    }

    const removeDatas = tabDatas.filter(tab => !filteredDatas.includes(tab));
    for (const removeData of removeDatas) {
      removeReduxViewState(removeData);
    }

    setData(filteredDatas);
    setSelectedTabIdx(tabIndex);

    updateReduxTabState(filteredDatas, tabIndex);

    handleContextMenuClose();
  }

  const closeAllTabs = () => {
    const { currentData } = contextMenu;

    const pinDatas = tabDatas.filter(tab => tab?.pin);

    let tabIndex = 0;
    if (currentData?.pin) {
      const currentIndex = pinDatas.findIndex(tab => isEqual(tab.key, currentData.key));
      tabIndex = currentIndex;
    } else {
      tabIndex = pinDatas.length - 1;
    }

    const removeDatas = tabDatas.filter(tab => !pinDatas.includes(tab));
    for (const removeData of removeDatas) {
      removeReduxViewState(removeData);
    }
    setData(pinDatas);
    setSelectedTabIdx(tabIndex);

    updateReduxTabState(pinDatas, tabIndex);

    handleContextMenuClose();
  }

  /*
   * 화면을 통째로 다시 마운트해서 초기 조회를 다시 태운다.
   * 예전에는 setData(tabDatas) 로 TabContainer 를 리렌더시키면 (본문 안에 정의돼 있던)
   * TabPanel 의 타입이 바뀌면서 서브트리가 remount 되는 **부작용**에 기대고 있었다.
   * TabPanel 이 모듈 스코프로 올라간 뒤로는 그 부작용이 사라져 새로고침이 무동작이 됐으므로,
   * TabPanel 의 key 를 바꿔 remount 를 **명시적으로** 요청한다.
   */
  const refresh = () => {
    const { currentData } = contextMenu;
    removeReduxViewState(currentData);
    requestRefresh(currentData?.key);

    handleContextMenuClose();
  }

  const position = isNull(contextMenu) ? null : { top: contextMenu.mouseY, left: contextMenu.mouseX };
  const isPinTap = contextMenu?.currentData?.pin;
  const existOnlyPinTab = tabDatas.filter((_, idx) => idx !== contextMenu?.dataIndex).findIndex(tab => !tab?.pin) === -1;
  // 우클릭한 탭이 지금 보고 있는 탭이 아닌 경우. 새로고침은 활성 탭에서만 허용한다.
  const isNotSelectedTab = !isEqual(selectedTabIdx, contextMenu?.dataIndex);

  return (
    <Menu
      id="context-menu"
      open={open}
      onClose={handleContextMenuClose}
      anchorReference="anchorPosition"
      anchorPosition={position}
    >
      {
        isPinTap ? (
          <MenuItem size="small" onClick={unpinTap}>
            <ListItemIcon>
              <PushPinOutlinedIcon />
            </ListItemIcon>
            <ListItemText>{t("unpin")}</ListItemText>
          </MenuItem>
        ) : (
          <MenuItem size="small" onClick={pinTab}>
            <ListItemIcon>
              <PushPinIcon />
            </ListItemIcon>
            <ListItemText>{t("pin")}</ListItemText>
          </MenuItem>
        )
      }
      <Divider />
      <MenuItem size="small" onClick={closeTap} disabled={isPinTap}>
        <ListItemText>{t("close")}</ListItemText>
      </MenuItem>
      <MenuItem size="small" onClick={closeOtherTabs} disabled={existOnlyPinTab}>
        <ListItemText>{t("closeOtherTabs")}</ListItemText>
      </MenuItem>
      <MenuItem size="small" onClick={closeAllTabs} disabled={isPinTap && existOnlyPinTab}>
        <ListItemText>{t("closeAllTabs")}</ListItemText>
      </MenuItem>
      <Divider />
      <MenuItem size="small" onClick={refresh} disabled={isNotSelectedTab}>
        <ListItemText>{t("refresh")}</ListItemText>
      </MenuItem>
    </Menu>
  );
};

const TabHeader = ({ t, data, setData, selectedTabIdx, setSelectedTabIdx, handleClose, saveReduxState,
                     getKeys, reduxDispatch, requestRefresh }) => {
  const [open, setOpen] = useState(false);
  const [contextMenu, setContextMenu] = useState(null);

  const handleChange = (_event, newValue) => {
    setSelectedTabIdx(newValue);

    reduxDispatch(reduxUtil.updateTabIndex({ tabIndex: newValue }));
  }

  const handleCloseEvent = (event, tabData) => {
    event.stopPropagation();
    handleClose(tabData);
  }

  const preventContextMenu = event => {
    event.preventDefault();
  }

  const handleContextMenu = (event, tabData, idx) => {
    preventContextMenu(event);
    const mouseXY = { mouseX: event.clientX + 2, mouseY: event.clientY - 6, currentData: tabData, dataIndex: idx };
    setContextMenu(mouseXY);
    setOpen(true);
  }

  const a11yProps = (index) => {
    return {
      id: `scrollable-auto-tab-${index}`,
      "aria-controls": `scrollable-auto-tabpanel-${index}`,
    };
  }

  return (
    <Box id="tab-header" onContextMenu={preventContextMenu}>
      <Tabs
        value={selectedTabIdx}
        onChange={handleChange}
        indicatorColor="primary"
        textColor="primary"
        variant="scrollable"
        scrollButtons="auto"
        aria-label="scrollable auto tabs"
        sx={{ marginTop: "6px", minHeight: "35px" }}
        onContextMenu={preventContextMenu}
      >
        {data.map((tabData, idx) => (
          <Tab className="tab" key={tabData.key} {...a11yProps(idx)} onContextMenu={(e) => handleContextMenu(e, tabData, idx)} onClick={() => saveReduxState(tabData)}
            label={
              <Box>{tabData.skipI18n ? (tabMenuList.getLocalizedTitle(tabData.reduxKey) ?? tabData.title) : t(tabData.title)}
                {tabData?.pin ? "" : <HighlightOffIcon className="close-icon" fontSize="small" onClick={(e) => handleCloseEvent(e, tabData)} />}
              </Box>} />
        ))}
      </Tabs>
      <CustomContextMenu t={t} data={data} setData={setData} selectedTabIdx={selectedTabIdx}
        setSelectedTabIdx={setSelectedTabIdx} handleClose={handleClose} getKeys={getKeys}
        reduxDispatch={reduxDispatch} open={open} setOpen={setOpen} contextMenu={contextMenu}
        requestRefresh={requestRefresh} />
    </Box>
  );
}

const TabContainer = ({ t, data, setData, handleClose, selectedTabIdx, setSelectedTabIdx, saveReduxState, getKeys }) => {
  const reduxDispatch = useDispatch();

  /*
   * 탭 key 별 새로고침 횟수. TabPanel 의 key 에 섞어 넣어 해당 탭만 remount 시킨다.
   * App 의 showedTabsInfo 를 건드리지 않는다 — 그쪽을 바꾸면 JSON.stringify(showedTabsInfo) 에
   * 걸린 App 의 effect 들(방문 이력 push 등)까지 덩달아 다시 돈다.
   */
  const [refreshSeq, setRefreshSeq] = useState({});

  const requestRefresh = key => {
    if (isNil(key)) {
      return;
    }
    setRefreshSeq(prev => ({ ...prev, [key]: (prev[key] ?? 0) + 1 }));
  };

  return (
    // height 100% — 상위가 flex row 가 아닌 래퍼(App 셸의 본문 슬롯)일 때도 높이를 잃지 않게 한다.
    <Box id={"tab-container"} sx={{ ml: 1, width: "100%", height: "100%", display: "flex", flexDirection: "column" }}>
      <Box sx={{ display: "flex", borderBottom: 1, borderColor: "divider", flexShrink: 0 }} >
        <TabHeader t={t} data={data} setData={setData} selectedTabIdx={selectedTabIdx}
          setSelectedTabIdx={setSelectedTabIdx} handleClose={handleClose} saveReduxState={saveReduxState}
          getKeys={getKeys} reduxDispatch={reduxDispatch} requestRefresh={requestRefresh} />
      </Box>
      <Box id={"tab-content"} sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "row" }}>
        <Box sx={{ flex: 1, minWidth: 0, height: "100%", overflow: "hidden" }}>
          {data.map((tabData, idx) => (
            <TabPanel value={selectedTabIdx} index={idx} module={getModuleScope(tabData.reduxKey)}
                      key={`${tabData.key}:${refreshSeq[tabData.key] ?? 0}`} >
              {tabData.component}
            </TabPanel>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

export default withTranslation()(TabContainer);
